import { useState, useEffect } from 'react';
import { 
  CreditCard,
  DollarSign,
  Percent,
  Save,
  AlertCircle,
  Mail,
  Settings,
  Trash2,
  Plus,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';

const API = `${window.location.origin}/api`;

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stripeKey, setStripeKey] = useState('');
  const [showStripeInput, setShowStripeInput] = useState(false);
  const [resendKey, setResendKey] = useState('');
  const [showResendInput, setShowResendInput] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (updates) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        toast.success('Settings saved successfully');
        fetchSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveStripeKey = async () => {
    if (!stripeKey.trim()) {
      toast.error('Please enter a Stripe API key');
      return;
    }
    await saveSettings({ stripe_api_key: stripeKey });
    setStripeKey('');
    setShowStripeInput(false);
  };

  const saveResendKey = async () => {
    if (!resendKey.trim()) {
      toast.error('Please enter a Resend API key');
      return;
    }
    await saveSettings({ resend_api_key: resendKey });
    setResendKey('');
    setShowResendInput(false);
  };

  const updatePackage = async (packages) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/settings/packages`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ packages })
      });

      if (response.ok) {
        toast.success('Packages updated successfully');
        fetchSettings();
      } else {
        toast.error('Failed to update packages');
      }
    } catch (error) {
      toast.error('Failed to update packages');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="h-8 w-48 skeleton rounded" />
        <div className="h-64 skeleton rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Configuration</p>
        <h1 className="text-3xl font-bold text-white tracking-tight">Platform Settings</h1>
        <p className="text-zinc-400 mt-2">Manage pricing, fees, and integrations</p>
      </div>

      {/* Stripe Configuration */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Stripe Configuration</h3>
            <p className="text-zinc-400 text-sm">Payment processing for MEL purchases</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <div>
              <p className="text-white font-medium">API Key Status</p>
              <p className="text-zinc-400 text-sm">
                {settings?.stripe_configured 
                  ? `Configured (${settings.stripe_api_key_preview})` 
                  : 'Not configured'}
              </p>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm ${
              settings?.stripe_configured 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-red-500/20 text-red-400'
            }`}>
              {settings?.stripe_configured ? 'Active' : 'Inactive'}
            </div>
          </div>

          {showStripeInput ? (
            <div className="space-y-3">
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">Stripe Secret Key</label>
                <Input
                  type="password"
                  value={stripeKey}
                  onChange={(e) => setStripeKey(e.target.value)}
                  placeholder="sk_live_... or sk_test_..."
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
                <p className="text-zinc-500 text-xs mt-1">
                  Find your API keys at <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">dashboard.stripe.com/apikeys</a>
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={saveStripeKey}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-500 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Key
                </button>
                <button
                  onClick={() => { setShowStripeInput(false); setStripeKey(''); }}
                  className="px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowStripeInput(true)}
              className="px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 transition-colors"
            >
              {settings?.stripe_configured ? 'Update Stripe Key' : 'Add Stripe Key'}
            </button>
          )}
        </div>
      </div>

      {/* Resend Configuration (Email Notifications) */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Email Notifications (Resend)</h3>
            <p className="text-zinc-400 text-sm">Send email notifications to users</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <div>
              <p className="text-white font-medium">Resend API Key Status</p>
              <p className="text-zinc-400 text-sm">
                {settings?.resend_configured 
                  ? `Configured (${settings.resend_api_key_preview})` 
                  : 'Not configured - email notifications disabled'}
              </p>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm ${
              settings?.resend_configured 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-yellow-500/20 text-yellow-400'
            }`}>
              {settings?.resend_configured ? 'Active' : 'In-App Only'}
            </div>
          </div>

          {showResendInput ? (
            <div className="space-y-3">
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">Resend API Key</label>
                <Input
                  type="password"
                  value={resendKey}
                  onChange={(e) => setResendKey(e.target.value)}
                  placeholder="re_..."
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
                <p className="text-zinc-500 text-xs mt-1">
                  Get your API key at <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">resend.com/api-keys</a>
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={saveResendKey}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Key
                </button>
                <button
                  onClick={() => { setShowResendInput(false); setResendKey(''); }}
                  className="px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowResendInput(true)}
              className="px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 transition-colors"
            >
              {settings?.resend_configured ? 'Update Resend Key' : 'Add Resend Key'}
            </button>
          )}

          <div className="p-4 rounded-lg bg-zinc-900/30 border border-zinc-800">
            <p className="text-zinc-400 text-sm">
              <strong className="text-white">Note:</strong> Without a Resend API key, users will only receive in-app notifications. 
              Email notifications will be enabled once you add a valid Resend API key.
            </p>
          </div>
        </div>
      </div>

      {/* Feature Toggles */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
            <Settings className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Feature Toggles</h3>
            <p className="text-zinc-400 text-sm">Enable or disable platform features</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Melanin Account Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <div className="flex-1">
              <p className="text-white font-medium">Melanin Account (Virtual USD)</p>
              <p className="text-zinc-400 text-sm">Allow users to create virtual USD accounts powered by MoonPay/Iron</p>
            </div>
            <button
              onClick={() => saveSettings({ melanin_account_enabled: !settings?.melanin_account_enabled })}
              disabled={saving}
              className={`relative w-14 h-8 rounded-full transition-colors ${
                settings?.melanin_account_enabled ? 'bg-green-500' : 'bg-zinc-700'
              }`}
              data-testid="toggle-melanin-account"
            >
              <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-transform ${
                settings?.melanin_account_enabled ? 'left-7' : 'left-1'
              }`} />
            </button>
          </div>

          {/* Marketplace Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <div className="flex-1">
              <p className="text-white font-medium">Marketplace (Products & Services)</p>
              <p className="text-zinc-400 text-sm">Allow claimed businesses to sell products and services</p>
            </div>
            <button
              onClick={() => saveSettings({ marketplace_enabled: !settings?.marketplace_enabled })}
              disabled={saving}
              className={`relative w-14 h-8 rounded-full transition-colors ${
                settings?.marketplace_enabled ? 'bg-green-500' : 'bg-zinc-700'
              }`}
              data-testid="toggle-marketplace"
            >
              <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-transform ${
                settings?.marketplace_enabled ? 'left-7' : 'left-1'
              }`} />
            </button>
          </div>

          {/* Delivery Service Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <div className="flex-1">
              <p className="text-white font-medium">Delivery Service</p>
              <p className="text-zinc-400 text-sm">Allow users to register as delivery drivers and fulfill orders</p>
            </div>
            <button
              onClick={() => saveSettings({ delivery_service_enabled: !settings?.delivery_service_enabled })}
              disabled={saving}
              className={`relative w-14 h-8 rounded-full transition-colors ${
                settings?.delivery_service_enabled ? 'bg-green-500' : 'bg-zinc-700'
              }`}
              data-testid="toggle-delivery-service"
            >
              <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-transform ${
                settings?.delivery_service_enabled ? 'left-7' : 'left-1'
              }`} />
            </button>
          </div>

          <div className="p-4 rounded-lg bg-zinc-900/30 border border-zinc-800">
            <p className="text-zinc-400 text-sm">
              <strong className="text-white">Note:</strong> Disabling a feature will hide it from users but will not delete existing data. 
              You can re-enable features at any time.
            </p>
          </div>
        </div>
      </div>

      {/* Platform Fees */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
            <Percent className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Platform Fees</h3>
            <p className="text-zinc-400 text-sm">Fee taken on transactions</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <label className="text-zinc-400 text-sm mb-2 block">Platform Fee %</label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={settings?.platform_fee_percent || 10}
                onChange={(e) => setSettings({ ...settings, platform_fee_percent: parseFloat(e.target.value) })}
                className="bg-zinc-900 border-zinc-800 text-white w-24"
                min="0"
                max="50"
                step="0.5"
              />
              <span className="text-zinc-400">%</span>
              <button
                onClick={() => saveSettings({ platform_fee_percent: settings?.platform_fee_percent })}
                disabled={saving}
                className="ml-auto px-3 py-1 rounded-lg bg-green-600 text-white text-sm hover:bg-green-500 transition-colors disabled:opacity-50"
              >
                Save
              </button>
            </div>
            <p className="text-zinc-500 text-xs mt-2">Fee taken on MEL transactions (boosts, purchases)</p>
          </div>
        </div>
      </div>

      {/* MEL Rewards Configuration */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">MEL Reward Amounts</h3>
            <p className="text-zinc-400 text-sm">How much MEL users earn for actions</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { key: 'melanin_business_submission', label: 'Business Verified', default: 0.5 },
            { key: 'melanin_review', label: 'Write Review', default: 0.1 },
            { key: 'melanin_domain_verification', label: 'Domain Verified', default: 2.0 },
            { key: 'referral_reward_referrer', label: 'Referral (Referrer)', default: 1.0 },
            { key: 'referral_reward_referee', label: 'Referral (New User)', default: 0.5 },
          ].map((item) => (
            <div key={item.key} className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
              <label className="text-zinc-400 text-sm mb-2 block">{item.label}</label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={settings?.[item.key] ?? item.default}
                  onChange={(e) => setSettings({ ...settings, [item.key]: parseFloat(e.target.value) })}
                  className="bg-zinc-900 border-zinc-800 text-white w-20"
                  min="0"
                  step="0.1"
                />
                <span className="text-zinc-400">MEL</span>
                <button
                  onClick={() => saveSettings({ [item.key]: settings?.[item.key] })}
                  disabled={saving}
                  className="ml-auto px-2 py-1 rounded bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
                >
                  <Save className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MEL Purchase Packages */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">MEL Purchase Packages</h3>
              <p className="text-zinc-400 text-sm">Packages users can buy with credit card</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {settings?.melanin_packages?.map((pkg, index) => (
            <div key={pkg.id} className="flex items-center gap-4 p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
              <div className="flex-1 grid grid-cols-3 gap-4">
                <div>
                  <label className="text-zinc-500 text-xs block mb-1">Label</label>
                  <Input
                    value={pkg.label}
                    onChange={(e) => {
                      const newPackages = [...settings.melanin_packages];
                      newPackages[index].label = e.target.value;
                      setSettings({ ...settings, melanin_packages: newPackages });
                    }}
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>
                <div>
                  <label className="text-zinc-500 text-xs block mb-1">Price (USD)</label>
                  <Input
                    type="number"
                    value={pkg.price_usd}
                    onChange={(e) => {
                      const newPackages = [...settings.melanin_packages];
                      newPackages[index].price_usd = parseFloat(e.target.value);
                      setSettings({ ...settings, melanin_packages: newPackages });
                    }}
                    className="bg-zinc-900 border-zinc-800 text-white"
                    min="0.5"
                    step="0.5"
                  />
                </div>
                <div>
                  <label className="text-zinc-500 text-xs block mb-1">MEL Amount</label>
                  <Input
                    type="number"
                    value={pkg.melanin}
                    onChange={(e) => {
                      const newPackages = [...settings.melanin_packages];
                      newPackages[index].melanin = parseFloat(e.target.value);
                      setSettings({ ...settings, melanin_packages: newPackages });
                    }}
                    className="bg-zinc-900 border-zinc-800 text-white"
                    min="1"
                    step="1"
                  />
                </div>
              </div>
              <button
                onClick={() => {
                  const newPackages = settings.melanin_packages.filter((_, i) => i !== index);
                  setSettings({ ...settings, melanin_packages: newPackages });
                }}
                className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          <div className="flex gap-2 mt-4">
            <button
              onClick={() => {
                const newPackage = {
                  id: `package_${Date.now()}`,
                  label: 'New Package',
                  price_usd: 10,
                  melanin: 10
                };
                setSettings({
                  ...settings,
                  melanin_packages: [...(settings.melanin_packages || []), newPackage]
                });
              }}
              className="px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Package
            </button>
            <button
              onClick={() => updatePackage(settings.melanin_packages)}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Packages
            </button>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
        <AlertCircle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-yellow-400 font-medium">Important Note</p>
          <p className="text-yellow-400/70 text-sm">
            Changes to pricing and fees take effect immediately. Stripe keys are stored securely and used for payment processing.
            For production, use your live Stripe keys (sk_live_...).
          </p>
        </div>
      </div>
    </div>
  );
};

export { AdminSettings };
export default AdminSettings;
